/**
 * Helpers for reading and writing FHIR `Extension` arrays on CPS
 * resources (PROD-642 Sprint 2.2).
 *
 * The Pathway Studio relies on two custom extensions:
 *   - `tenant-id` for multi-tenant isolation across CPS / PA / PM
 *   - `risk-tier` on each ActivityDefinition per ADR-001
 *
 * The helpers below are pure - no side effects, no I/O. They are the
 * single source of truth for the extension URL constants and the
 * read / write semantics so the FHIR client and the mappers do not
 * each re-implement extension navigation.
 *
 * HC5: zero em / en dashes anywhere.
 */
import {
  RISK_TIER_EXTENSION_URL,
  TENANT_EXTENSION_URL,
  type FhirExtension,
  type FhirResource,
} from './types';
import { TenantMismatchError } from './errors';

/**
 * Read the `tenant-id` extension off a resource. Returns `null` when
 * the extension is not present so callers can distinguish "absent"
 * from "wrong tenant".
 */
export function readTenantId(resource: FhirResource): string | null {
  const ext = resource.extension?.find((e) => e.url === TENANT_EXTENSION_URL);
  if (!ext) return null;
  return typeof ext.valueString === 'string' ? ext.valueString : null;
}

/**
 * Set the `tenant-id` extension on a resource. Returns a new resource;
 * does not mutate the input. Existing `tenant-id` entries are replaced.
 */
export function withTenantId<T extends FhirResource>(
  resource: T,
  tenantId: string,
): T {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== TENANT_EXTENSION_URL,
  );
  const tenantExt: FhirExtension = {
    url: TENANT_EXTENSION_URL,
    valueString: tenantId,
  };
  return {
    ...resource,
    extension: [...others, tenantExt],
  };
}

/**
 * Assert that the resource's `tenant-id` extension matches the
 * requested tenant. Throws `TenantMismatchError` on mismatch or
 * when the extension is absent (defense-in-depth: a missing tenant
 * extension is treated as a hard rejection rather than a permissive
 * read).
 */
export function assertTenantMatches(
  resource: FhirResource,
  requestedTenantId: string,
): void {
  const actual = readTenantId(resource);
  if (actual !== requestedTenantId) {
    throw new TenantMismatchError(
      resource.resourceType,
      resource.id,
      requestedTenantId,
      actual,
    );
  }
}

/** Risk-tier vocabulary per ADR-001. */
export type RiskTier = 'tier-1' | 'tier-2' | 'tier-3' | 'tier-4';

/** Read the `risk-tier` extension. Returns null if absent or malformed. */
export function readRiskTier(resource: FhirResource): RiskTier | null {
  const ext = resource.extension?.find(
    (e) => e.url === RISK_TIER_EXTENSION_URL,
  );
  if (!ext) return null;
  const raw = ext.valueCode ?? ext.valueString;
  if (
    raw === 'tier-1' ||
    raw === 'tier-2' ||
    raw === 'tier-3' ||
    raw === 'tier-4'
  ) {
    return raw;
  }
  return null;
}

/**
 * Set the `risk-tier` extension on an ActivityDefinition (or any
 * resource that needs it). Returns a new resource; does not mutate.
 */
export function withRiskTier<T extends FhirResource>(
  resource: T,
  tier: RiskTier,
): T {
  const others = (resource.extension ?? []).filter(
    (e) => e.url !== RISK_TIER_EXTENSION_URL,
  );
  const tierExt: FhirExtension = {
    url: RISK_TIER_EXTENSION_URL,
    valueCode: tier,
  };
  return {
    ...resource,
    extension: [...others, tierExt],
  };
}
